import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { User, Session } from '@supabase/supabase-js';
import { toast } from '@/hooks/use-toast';
import { withRateLimit, RateLimitPresets } from '@/lib/rateLimiter';
import { passwordSchema, emailSchema } from '@/lib/validationSchemas';
import { clearAllAuthCaches } from '@/lib/authCache';
import { logger } from '@/lib/logger';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isRecoveryMode: boolean;
  signUp: (email: string, password: string, displayName?: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
  // Legacy compatibility
  isAuthenticated: boolean;
  login: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isRecoveryMode, setIsRecoveryMode] = useState(() => {
    // Check both sessionStorage AND URL on initial load
    const fromStorage = sessionStorage.getItem('password_recovery_mode') === 'true';
    const inviteStorage = sessionStorage.getItem('invite_mode') === 'true';
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const queryParams = new URLSearchParams(window.location.search);
    const type = hashParams.get('type') || queryParams.get('type');
    const tokenHash = hashParams.get('token_hash') || queryParams.get('token_hash');
    // Recovery OR invite mode
    const fromURL = type === 'recovery' || type === 'invite' || !!tokenHash;
    
    return fromStorage || inviteStorage || fromURL;
  });

  // Sync recovery mode to sessionStorage - ONLY SET, NEVER AUTO-REMOVE
  // Removal is done explicitly after successful password update
  useEffect(() => {
    if (isRecoveryMode) {
      // Only set if not already in invite mode
      if (sessionStorage.getItem('invite_mode') !== 'true') {
        sessionStorage.setItem('password_recovery_mode', 'true');
      }
    }
    // DO NOT remove sessionStorage here - it causes race conditions
  }, [isRecoveryMode]);

  useEffect(() => {
    let isMounted = true;
    
    // Function to process recovery/invite tokens from URL
    const processRecoveryToken = async () => {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const queryParams = new URLSearchParams(window.location.search);
      
      // Check URL first
      let type = hashParams.get('type') || queryParams.get('type');
      let tokenHash = hashParams.get('token_hash') || queryParams.get('token_hash');
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');
      const errorDesc = hashParams.get('error_description') || queryParams.get('error_description');
      const errorCode = hashParams.get('error_code') || queryParams.get('error_code');
      
      // FALLBACK: If URL is empty, check sessionStorage for saved token
      const isInviteFromStorage = sessionStorage.getItem('invite_mode') === 'true';
      const isRecoveryFromStorage = sessionStorage.getItem('password_recovery_mode') === 'true';
      
      if (!tokenHash && (isRecoveryFromStorage || isInviteFromStorage)) {
        const savedTokenHash = sessionStorage.getItem('recovery_token_hash');
        if (savedTokenHash) {
          logger.log('[Auth] Using saved recovery token');
          tokenHash = savedTokenHash;
          type = isInviteFromStorage ? 'invite' : 'recovery';
        }
      }
      
      logger.log('[Auth] processRecoveryToken:', { type, hasTokenHash: !!tokenHash, hasAccessToken: !!accessToken, errorCode });
      
      // Check for errors first
      if (errorDesc || errorCode) {
        logger.error('[Auth] Token error detected');
        sessionStorage.removeItem('password_recovery_mode');
        sessionStorage.removeItem('invite_mode');
        sessionStorage.removeItem('recovery_token_hash');
        return { hasError: true, errorMessage: errorDesc || 'Link expirado ou inválido' };
      }
      
      // Invite or Recovery mode detection
      if (type === 'invite' || type === 'recovery' || tokenHash) {
        const isInvite = type === 'invite';
        logger.log('[Auth] Auth setup detected - type:', type, 'isInvite:', isInvite);
        
        if (isMounted) {
          setIsRecoveryMode(true);
          if (isInvite) {
            sessionStorage.setItem('invite_mode', 'true');
            sessionStorage.removeItem('password_recovery_mode');
          } else {
            sessionStorage.setItem('password_recovery_mode', 'true');
            sessionStorage.removeItem('invite_mode');
          }
        }
        
        // PKCE Flow: Use verifyOtp for token_hash
        if (tokenHash) {
          logger.log('[Auth] PKCE flow - verifying token');
          try {
            const { data, error } = await supabase.auth.verifyOtp({
              token_hash: tokenHash,
              type: isInvite ? 'invite' : 'recovery'
            });
            
            if (error) {
              logger.error('[Auth] verifyOtp failed');
              sessionStorage.removeItem('password_recovery_mode');
              sessionStorage.removeItem('invite_mode');
              sessionStorage.removeItem('recovery_token_hash');
              return { hasError: true, errorMessage: error.message };
            }
            
            logger.log('[Auth] verifyOtp successful');
            
            // Clean up after successful verification
            sessionStorage.removeItem('recovery_token_hash');
            window.history.replaceState({}, '', '/auth');
            
            return { isRecovery: true, isInvite, session: data.session };
          } catch (e: any) {
            logger.error('[Auth] Exception during verifyOtp');
            sessionStorage.removeItem('password_recovery_mode');
            sessionStorage.removeItem('invite_mode');
            sessionStorage.removeItem('recovery_token_hash');
            return { hasError: true, errorMessage: e.message };
          }
        }
        
        // Legacy flow: access_token + refresh_token in hash
        if (accessToken && refreshToken) {
          logger.log('[Auth] Legacy flow - setting session');
          try {
            const { error } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken
            });
            if (error) {
              logger.error('[Auth] Failed to set session');
              sessionStorage.removeItem('password_recovery_mode');
              sessionStorage.removeItem('invite_mode');
              return { hasError: true, errorMessage: error.message };
            }
            logger.log('[Auth] Session set successfully');
            window.history.replaceState({}, '', '/auth');
            return { isRecovery: true, isInvite };
          } catch (e: any) {
            logger.error('[Auth] Exception setting session');
            sessionStorage.removeItem('password_recovery_mode');
            sessionStorage.removeItem('invite_mode');
            return { hasError: true, errorMessage: e.message };
          }
        }
        
        return { isRecovery: true, isInvite };
      }
      
      return { isRecovery: false, isInvite: false };
    };

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!isMounted) return;
        
        logger.log('[Auth] Auth state change:', event, session ? 'Authenticated' : 'No session');
        
        // Check if we're in recovery or invite mode
        const inRecovery = sessionStorage.getItem('password_recovery_mode') === 'true';
        const inInvite = sessionStorage.getItem('invite_mode') === 'true';
        const inAuthSetup = inRecovery || inInvite;
        
        // Always update session
        setSession(session);
        
        // Detect PASSWORD_RECOVERY event - this is the key Supabase event
        if (event === 'PASSWORD_RECOVERY') {
          logger.log('[Auth] PASSWORD_RECOVERY event received');
          setIsRecoveryMode(true);
          sessionStorage.setItem('password_recovery_mode', 'true');
          setLoading(false);
          return; // Don't set user in recovery
        }
        
        // In auth setup mode, DON'T set user to prevent auto-redirect
        if (!inAuthSetup) {
          setUser(session?.user ?? null);
        } else {
          logger.log('[Auth] In auth setup mode - not setting user');
        }
        
        setLoading(false);
      }
    );

    // Process tokens and get initial session
    processRecoveryToken().then(async (result) => {
      if (!isMounted) return;
      
      if (result.hasError) {
        logger.error('[Auth] Recovery token error');
        setLoading(false);
        return;
      }
      
      // Get current session
      const { data: { session } } = await supabase.auth.getSession();
      const inRecovery = sessionStorage.getItem('password_recovery_mode') === 'true';
      const inInvite = sessionStorage.getItem('invite_mode') === 'true';
      const inAuthSetup = inRecovery || inInvite;
      
      logger.log('[Auth] Initial session check:', session ? 'Found' : 'None', 'inAuthSetup:', inAuthSetup);
      
      if (isMounted) {
        setSession(session);
        if (!inAuthSetup) {
          setUser(session?.user ?? null);
        }
        setLoading(false);
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const createUserProfile = async (user: User) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .insert([
          {
            user_id: user.id,
            display_name: user.user_metadata?.display_name || user.email?.split('@')[0],
          }
        ]);

      if (error) throw error;
    } catch (error: any) {
      logger.error('Error creating user profile');
    }
  };

  const signUp = async (email: string, password: string, displayName?: string) => {
    // Validar email
    const emailValidation = emailSchema.safeParse(email);
    if (!emailValidation.success) {
      toast({
        variant: "destructive",
        title: "Email inválido",
        description: emailValidation.error.errors[0].message,
      });
      throw new Error(emailValidation.error.errors[0].message);
    }

    // Validar força da senha
    const passwordValidation = passwordSchema.safeParse(password);
    if (!passwordValidation.success) {
      toast({
        variant: "destructive",
        title: "Senha fraca",
        description: passwordValidation.error.errors[0].message,
      });
      throw new Error(passwordValidation.error.errors[0].message);
    }

    // Aplicar rate limiting
    return withRateLimit(
      `signup_${email}`,
      async () => {
        const redirectUrl = `${window.location.origin}/`;
        
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: redirectUrl,
            data: {
              display_name: displayName,
            },
          },
        });

        if (error) {
          toast({
            variant: "destructive",
            title: "Erro no cadastro",
            description: error.message,
          });
          throw error;
        }

        toast({
          title: "Cadastro realizado",
          description: "Verifique seu email para confirmar a conta.",
        });
      },
      RateLimitPresets.AUTH
    );
  };

  const signIn = async (email: string, password: string) => {
    // Aplicar rate limiting para prevenir brute force
    return withRateLimit(
      `signin_${email}`,
      async () => {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) {
          toast({
            variant: "destructive",
            title: "Erro no login",
            description: error.message,
          });
          throw error;
        }
      },
      RateLimitPresets.AUTH
    );
  };

  const resetPassword = async (email: string) => {
    const redirectUrl = `${window.location.origin}/auth`;
    
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl,
    });

    if (error) {
      toast({
        variant: "destructive",
        title: "Erro ao enviar email",
        description: error.message,
      });
      throw error;
    }

    toast({
      title: "Email enviado",
      description: "Verifique seu email para redefinir sua senha.",
    });
  };

  const updatePassword = async (newPassword: string) => {
    // First, check if we have an active session
    const { data: { session: currentSession } } = await supabase.auth.getSession();
    
    if (!currentSession) {
      // No active session - token expired or was invalidated
      setIsRecoveryMode(false);
      sessionStorage.removeItem('password_recovery_mode');
      sessionStorage.removeItem('invite_mode');
      toast({
        variant: "destructive",
        title: "Sessão expirada",
        description: "O link expirou. Por favor, solicite um novo link.",
      });
      throw new Error('Sessão expirada. Solicite um novo link.');
    }

    // Validate password strength
    const passwordValidation = passwordSchema.safeParse(newPassword);
    if (!passwordValidation.success) {
      toast({
        variant: "destructive",
        title: "Senha fraca",
        description: passwordValidation.error.errors[0].message,
      });
      throw new Error(passwordValidation.error.errors[0].message);
    }

    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });

    if (error) {
      // Check if it's a session-related error
      if (error.message.toLowerCase().includes('session')) {
        setIsRecoveryMode(false);
        sessionStorage.removeItem('password_recovery_mode');
        sessionStorage.removeItem('invite_mode');
        toast({
          variant: "destructive",
          title: "Sessão expirada",
          description: "O link expirou. Por favor, solicite um novo link.",
        });
        throw new Error('Sessão expirada. Solicite um novo link.');
      }
      
      toast({
        variant: "destructive",
        title: "Erro ao atualizar senha",
        description: error.message,
      });
      throw error;
    }

    // Clear auth setup mode from both state and sessionStorage
    setIsRecoveryMode(false);
    sessionStorage.removeItem('password_recovery_mode');
    sessionStorage.removeItem('invite_mode');
    
    toast({
      title: "Senha atualizada",
      description: "Sua senha foi redefinida com sucesso. Você já está logado.",
    });
  };

  const signOut = async () => {
    // Save userId before clearing session to clean up caches
    const currentUserId = user?.id;
    
    try {
      // Check if there is an active session first
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        // No active session: clear local tokens and proceed silently
        await supabase.auth.signOut({ scope: 'local' }).catch(() => {});
        setUser(null);
        // Clear caches for security
        if (currentUserId) {
          clearAllAuthCaches(currentUserId);
        }
        return;
      }

      const { error } = await supabase.auth.signOut(); // global by default
      if (error) {
        // Treat common "no session" cases as success to avoid noisy errors
        const msg = (error.message || '').toLowerCase();
        if (msg.includes('auth session missing') || msg.includes('session_not_found')) {
          await supabase.auth.signOut({ scope: 'local' }).catch(() => {});
          setUser(null);
          // Clear caches for security
          if (currentUserId) {
            clearAllAuthCaches(currentUserId);
          }
          return;
        }
        throw error;
      }

      setUser(null);
      // Clear caches for security after successful logout
      if (currentUserId) {
        clearAllAuthCaches(currentUserId);
      }
    } catch (error: any) {
      // As a fallback, clear local auth state and continue
      try { await supabase.auth.signOut({ scope: 'local' }); } catch {}
      setUser(null);
      // Clear caches even on error for security
      if (currentUserId) {
        clearAllAuthCaches(currentUserId);
      }
      logger.error('Logout error');
    }
  };

  // Legacy compatibility methods
  const login = () => {
    // This is a placeholder for legacy compatibility
    // Real login should use signIn method
  };

  const logout = async () => {
    await signOut();
  };

  const value = {
    user,
    session,
    loading,
    isRecoveryMode,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updatePassword,
    // Legacy compatibility
    isAuthenticated: !!user,
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};